import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ShimmerColumn {
  label: string;
  width: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Displays a configurable shimmering placeholder table used while real table data is loading.
 * Renders a skeleton table with a configurable number of rows and column definitions. Key methods:
 * shimmerRows (getter) - produces an array of row indices for rendering placeholders.
 * getColumnClass(column: ShimmerColumn) - returns a CSS class name derived from a column's width for per-cell styling.
 * No Angular lifecycle hooks (OnInit, OnDestroy, etc.) are implemented here — the component is purely presentational.
 * @component
 * @selector app-loading-table-shimmer
 * @example
 *   <app-loading-table-shimmer [columns]="columns" [rows]="10" [showHeader]="true" [className]="'my-shimmer'"></app-loading-table-shimmer>
 * @input ShimmerColumn[] columns - Array of column definitions used to render the shimmering cells (each column typically contains width and other display hints).
 * @input number rows - Number of shimmer rows to render (defaults to 5).
 * @input boolean showHeader - Whether to render a shimmering header row (defaults to true).
 * @input string className - Optional additional CSS class(es) applied to the root element for custom styling.
 * @output none - This component does not emit any output events; it is a visual placeholder only.
 */
@Component({
  selector: 'app-loading-table-shimmer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './loading-table-shimmer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./loading-table-shimmer.component.scss'],
})
export class LoadingTableShimmerComponent {
  @Input() columns: ShimmerColumn[] = [];
  @Input() rows = 5;
  @Input() showHeader = true;
  @Input() className = '';
  /** `data-testid` for the loading placeholder, so automation can await the loaded state. */
  @Input() testId = 'loading-shimmer';

  get shimmerRows(): number[] {
    return Array(this.rows)
      .fill(0)
      .map((_, i) => i);
  }

  getColumnClass(column: ShimmerColumn): string {
    return `shimmer-cell-${column.width}`;
  }

  getGridTemplateColumns(): string {
    if (!this.columns || this.columns.length === 0) {
      return 'repeat(auto-fit, minmax(120px, 1fr))';
    }
    return `repeat(${this.columns.length}, minmax(120px, 1fr))`;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
